import { ClaimCloseoutType, CloseoutFormValues, mapWithConcurrency, resolveCloseoutVoid } from '@enter/core';

export interface ClaimCloseoutBatchTarget {
    id: string;
    /**
     * Current void state, read before the batch runs — a void cannot be reopened
     * (VOID_CLAIM_HELPER). Required rather than optional: an unknown state must never be
     * treated as "not voided" by `resolveCloseoutVoid`, which is what an omitted value would do.
     */
    closeoutVoid: boolean;
}

export interface ClaimCloseoutAttemptResult {
    claimId: string;
    ok: boolean;
    error?: string;
}

/**
 * The payload actually sent for one claim. A selection routinely mixes already-voided claims with
 * ones being reopened, so a claim that is voided and asked to reopen must fail on its own rather
 * than silently staying voided (misreporting the batch as fully applied) or silently reopening
 * (undoing an irreversible void).
 */
export function resolveBatchCloseoutPayload(
    target: ClaimCloseoutBatchTarget,
    values: CloseoutFormValues,
): CloseoutFormValues {
    if (target.closeoutVoid && values.closeoutType !== ClaimCloseoutType.CLOSED) {
        throw new Error('Voided claims cannot be reopened.');
    }

    const closeoutVoid = resolveCloseoutVoid(values.closeoutType, values.closeoutVoid, target.closeoutVoid);
    const closeoutType = closeoutVoid ? ClaimCloseoutType.CLOSED : values.closeoutType;

    return {
        closeoutType,
        closeoutDescription: values.closeoutDescription,
        closeoutVoid,
    };
}

export async function runBatchCloseout(
    targets: ClaimCloseoutBatchTarget[],
    values: CloseoutFormValues,
    update: (claimId: string, payload: CloseoutFormValues) => Promise<unknown>,
    concurrency = 10,
    onProgress?: (completed: number, total: number) => void,
): Promise<ClaimCloseoutAttemptResult[]> {
    return mapWithConcurrency(
        targets,
        concurrency,
        async target => {
            try {
                const payload = resolveBatchCloseoutPayload(target, values);
                await update(target.id, payload);
                return { claimId: target.id, ok: true };
            } catch (error) {
                return {
                    claimId: target.id,
                    ok: false,
                    error: error instanceof Error ? error.message : String(error),
                };
            }
        },
        onProgress,
    );
}

export function partitionCloseoutResults(results: ClaimCloseoutAttemptResult[]): {
    succeeded: ClaimCloseoutAttemptResult[];
    failed: ClaimCloseoutAttemptResult[];
} {
    return {
        succeeded: results.filter(result => result.ok),
        failed: results.filter(result => !result.ok),
    };
}
